import React, { useSyncExternalStore } from 'react';

import { DynamicAudienceSection } from '@occc-app/routes/Audience/constants';
import { createStore, SectionsState } from './store';

export type AudienceExpandedSectionsContextValue = {
  store: ReturnType<typeof createStore>;
  onExpandedChange: (section: DynamicAudienceSection, expanded: boolean) => void;
  onMultipleExpandedChange: (
    newExpandedSectionsMap: Partial<Record<DynamicAudienceSection, boolean>>,
  ) => void;
};

const AudienceExpandedSectionsContext = React.createContext<
  AudienceExpandedSectionsContextValue | undefined
>(undefined);

type AudienceExpandedSectionsProviderProps = {
  children?: React.ReactNode;
};

export const AudienceExpandedSectionsProvider = ({
  children,
}: AudienceExpandedSectionsProviderProps) => {
  const [store] = React.useState(() => createStore({}));

  const onExpandedChange = React.useCallback(
    (section: DynamicAudienceSection, expanded: boolean) => {
      store.setState((expandedSectionsMap) => {
        return {
          ...expandedSectionsMap,
          [section]: expanded,
        };
      });
    },
    [store],
  );

  const onMultipleExpandedChange = React.useCallback(
    (newExpandedSectionsMap: Partial<Record<DynamicAudienceSection, boolean>>) => {
      store.setState((expandedSectionsMap) => {
        return {
          ...expandedSectionsMap,
          ...newExpandedSectionsMap,
        };
      });
    },
    [store],
  );

  const value = React.useMemo(() => {
    return {
      store,
      onExpandedChange,
      onMultipleExpandedChange,
    };
  }, [store, onExpandedChange, onMultipleExpandedChange]);

  return (
    <AudienceExpandedSectionsContext.Provider value={value}>
      {children}
    </AudienceExpandedSectionsContext.Provider>
  );
};

export const useExpandedSections = () => {
  const contextValue = React.useContext(AudienceExpandedSectionsContext);

  if (contextValue === undefined) {
    throw new Error('useExpandedSections must be used within an AudienceExpandedSectionsProvider');
  }

  return contextValue;
};

export const useSectionState = (key: keyof SectionsState) => {
  const { store } = useExpandedSections();

  return useSyncExternalStore(
    store.subscribe,
    () => store.getSnapshot()[key] ?? false,
    () => store.getSnapshot()[key] ?? false,
  );
};
